import { TemperatureSensorModel } from './temperature-sensor-model';
import { PumpSensorModel } from './pump-sensor-model';
import { MeterSensorModel } from './meter-sensor-model';
import { PressureSensorModel } from './perssure-sensor-model';
import { GatewayStyleEnum } from './gateway-style.enum';
import { UvsModuleModel } from './uvs-module.model';
import { OtherSensorModel } from './other-sensor.model';
import { GatewayType } from '../interfaces/juconnGateway.response';
import { IInstallationDataResponse } from '../interfaces/installation-data.response';
import { PfcSensorModel } from './pfc-sensor.model';
import { ShareService } from '../services/share.service';
import { SensorModeType } from '../enums/sensor-mode-type';

export class CollectedDataModel {
  gatewayId: string;
  simIp: string;

  gatewayStyle: GatewayStyleEnum;
  additionalGateway: boolean;
  withHeating: boolean;

  customerId?: number;
  siteId?: number;
  city: string;
  postCode: string;
  street: string;
  streetNumber: string;
  heatingLocation: string;
  contactPersonName: string;
  contactPersonNumber: string;
  mainMeterReading: string;
  mainMeterReadingUnit: string;
  comment: string;

  heatings: HeatingModel[];
  heatingChecks: HeatingCheckModel;

  preInstallBoilerRoomImages: { image: string }[] = [];
  preInstallHeatingImages: { image: string }[] = [];
  postInstallBoilerRoomImages: { image: string }[] = [];
  postInstallHeatingImages: { image: string }[] = [];
  sensorImages: string[] = [];
  signImage: string;
  signImageRaw: any;
  signature: string;
  signatureDate: string; // iso string
  installationAt: string; // iso string
  installationInSeveralRooms: boolean;

  verificationSuccessful: boolean;

  installationBoxImage: string;
  antennaImage: string;
  heatingControlImage: string;
  heatingControlType: string;
  heatingCollectorImage: string;

  heatingRoomLength: number;
  heatingRoomWidth: number;
  heatingShutdownPossible: boolean;
  heatingSpaceAvailable: boolean;

  schemeImage: string;

  uvsModules: UvsModuleModel[] = [];
  temperatureSensors: TemperatureSensorModel[] = [];
  pumpSensors: PumpSensorModel[] = [];
  meterSensors: MeterSensorModel[] = [];
  meterKeys: string[] = [];
  pfcSensors: PfcSensorModel[] = [];

  pressureSensors: PressureSensorModel[] = [];

  otherSensors: OtherSensorModel[] = [];

  commissioningMaterial: MaterialModel;
  installationMaterial: MaterialModel;

  technicianEmail: string;

  public static initializeCollectedData(
    collectedData: CollectedDataModel,
  ): void {
    if (
      collectedData.gatewayStyle === null ||
      collectedData.gatewayStyle === undefined
    ) {
      collectedData.gatewayStyle = GatewayStyleEnum.JUCONN;
    }

    if (
      collectedData.additionalGateway === null ||
      collectedData.additionalGateway === undefined
    ) {
      collectedData.additionalGateway = false;
      collectedData.withHeating = true;
    }

    if (!collectedData.preInstallBoilerRoomImages) {
      collectedData.preInstallBoilerRoomImages = [];
    }
    while (collectedData.preInstallBoilerRoomImages.length < 3) {
      collectedData.preInstallBoilerRoomImages.push({ image: null });
    }

    if (
      !collectedData.preInstallHeatingImages ||
      collectedData.preInstallHeatingImages.length === 0
    ) {
      collectedData.preInstallHeatingImages = [{ image: null }];
    }
    if (
      !collectedData.postInstallHeatingImages ||
      collectedData.postInstallHeatingImages.length === 0
    ) {
      collectedData.postInstallHeatingImages = [{ image: null }];
    }

    if (!(collectedData.postInstallBoilerRoomImages?.length < 3)) {
      collectedData.postInstallBoilerRoomImages =
        collectedData.postInstallBoilerRoomImages || [];
      while (collectedData.postInstallBoilerRoomImages.length < 3) {
        collectedData.postInstallBoilerRoomImages.push({ image: null });
      }
    }

    if (!collectedData.postInstallHeatingImages?.length) {
      collectedData.postInstallHeatingImages =
        collectedData.postInstallHeatingImages || [];
      while (
        collectedData.postInstallHeatingImages.length <
        collectedData.preInstallHeatingImages.length
      ) {
        collectedData.postInstallHeatingImages.push({ image: null });
      }
    }

    if (!collectedData.heatings) {
      collectedData.heatings = [new HeatingModel()];
    }

    if (!collectedData.heatingChecks) {
      collectedData.heatingChecks = new HeatingCheckModel();
      collectedData.heatingChecks.insulatingCaps = null;
      collectedData.heatingChecks.insulatedPipes = null;
      collectedData.heatingChecks.averageRoomTemperature = null;
      collectedData.heatingChecks.outdoorTemperatureAvailable = null;
      collectedData.heatingChecks.summerWinterOperationAvailable = null;
      collectedData.heatingChecks.emergencyStopAvailable = null;
    }

    if (!collectedData.uvsModules) {
      console.log('initializeCollectedData: uvsModules');
      collectedData.uvsModules = [];
      collectedData.uvsModules.push({
        modulId: '1',
        uvsType: GatewayType.SM01,
        address: 1,
      });
    }

    if (!collectedData.commissioningMaterial) {
      collectedData.commissioningMaterial = new MaterialModel();
      collectedData.commissioningMaterial.materialType = 'COMMISSIONING';
    }
    if (!collectedData.installationMaterial) {
      collectedData.installationMaterial = new MaterialModel();
      collectedData.installationMaterial.materialType = 'INSTALLATION';
    }
  }

  public static setCollectedDataFromInstalledData(
    collectedData: CollectedDataModel,
    installedData: IInstallationDataResponse,
  ): void {
    collectedData.installationAt =
      installedData.installationAt || collectedData.installationAt;
    collectedData.gatewayId = installedData.serialNo || collectedData.gatewayId;
    collectedData.technicianEmail =
      installedData.technicianEmail || collectedData.technicianEmail;
    collectedData.customerId =
      installedData.productSpecificData?.customerId || collectedData.customerId;
    collectedData.siteId =
      installedData.productSpecificData?.siteId || collectedData.siteId;
    collectedData.comment =
      installedData.productSpecificData?.comment || collectedData.comment;

    const installationMaterial =
      installedData.productSpecificData?.materials?.find(
        (m) => m.materialType === 'INSTALLATION',
      );
    const commissioningMaterial = JSON.parse(
      JSON.stringify(
        installedData.productSpecificData?.materials?.find(
          (m) => m.materialType === 'INSTALLATION',
        ),
      ),
    );
    commissioningMaterial.materialType = 'COMMISSIONING';

    collectedData.installationMaterial =
      installationMaterial || collectedData.installationMaterial;
    collectedData.commissioningMaterial =
      commissioningMaterial || collectedData.commissioningMaterial;

    collectedData.preInstallHeatingImages =
      installedData.productSpecificData?.preInstallHeatingImages?.map(
        (image) => ({ image: image.image }),
      ) || collectedData.preInstallHeatingImages;
    collectedData.preInstallBoilerRoomImages =
      installedData.productSpecificData?.preInstallBoilerImages?.map(
        (image) => ({ image: image.image }),
      ) || collectedData.preInstallBoilerRoomImages;

    const uvsModules = [];
    if (installedData.productSpecificData?.uvsModules?.length > 0) {
      for (const uvsModule of installedData.productSpecificData.uvsModules) {
        uvsModules.push({
          modulId: uvsModule.modulId,
          uvsType: uvsModule.uvsType,
          address: uvsModule.address,
        });
      }
    }
    if (!!uvsModules.length) {
      collectedData.uvsModules = uvsModules;
    }
  }

  public static buildFromDataModels(
    shareService: ShareService,
  ): CollectedDataModel {
    const collectedData = new CollectedDataModel();
    CollectedDataModel.initializeCollectedData(collectedData);
    // Map Building data
    const building = shareService.building;
    if (building) {
      collectedData.heatingLocation = building.heatingLocation;
      collectedData.contactPersonName = building.contactPersonName;
      collectedData.contactPersonNumber = building.contactPersonNumber;
      collectedData.mainMeterReading = building.mainMeterReading;
      collectedData.mainMeterReadingUnit = building.mainMeterReadingUnit;
    }

    // Map General Gateway data
    const generalGateway = shareService.generalGateway;
    if (generalGateway) {
      collectedData.city = generalGateway.selectedSite.city;
      collectedData.postCode = generalGateway.selectedSite.postCode;
      collectedData.street = generalGateway.selectedSite.street;
      collectedData.streetNumber = generalGateway.selectedSite.streetNumber;
      collectedData.siteId = generalGateway.selectedSite.id;
      collectedData.customerId = generalGateway.selectedCustomer?.customerId;
      collectedData.additionalGateway = generalGateway.additionalGateway;
      collectedData.uvsModules = generalGateway.uvsModules;
    }

    // Map General Measurement data
    const generalMeasurement = shareService.generalMeasurement;
    if (generalMeasurement) {
      collectedData.installationMaterial =
        generalMeasurement.installationMaterial;
    }

    // Map General Pre Photos data
    const generalPrePhotos = shareService.generalPrePhotos;
    if (generalPrePhotos) {
      collectedData.preInstallBoilerRoomImages =
        generalPrePhotos.preInstallBoilerRoomImages;
      collectedData.preInstallHeatingImages =
        generalPrePhotos.preInstallHeatingImages;
    }

    // Map General Sending data
    const generalSending = shareService.generalSending;
    if (generalSending) {
      collectedData.comment = generalSending.generalComment;
    }

    // Map Heating Check data
    const heatingCheck = shareService.heatingCheck;
    if (heatingCheck) {
      collectedData.heatingChecks = {
        averageRoomTemperature: heatingCheck.averageRoomTemperature,
        outdoorTemperatureAvailable: heatingCheck.outdoorTemperatureAvailable,
        summerWinterOperationAvailable:
          heatingCheck.summerWinterOperationAvailable,
        emergencyStopAvailable: heatingCheck.emergencyStopAvailable,
        insulatedPipes: heatingCheck.insulatedPipes,
        insulatingCaps: heatingCheck.insulatingCaps,
      };
    }

    // Map Heating Information data
    const heatingInformation = shareService.heatingInformation;
    if (heatingInformation) {
      collectedData.withHeating = heatingInformation.withHeating;
      collectedData.heatingControlType = heatingInformation.heatingControlType;
      collectedData.heatingControlImage =
        heatingInformation.heatingControlImage;
      collectedData.heatingRoomLength = heatingInformation.heatingRoomLength;
      collectedData.heatingRoomWidth = heatingInformation.heatingRoomWidth;
      collectedData.heatingShutdownPossible =
        heatingInformation.heatingShutdownPossible;
      collectedData.heatingSpaceAvailable =
        heatingInformation.heatingSpaceAvailable;
      collectedData.heatings = heatingInformation.heatings;
    }

    // Map Photos data
    const photos = shareService.photos;
    if (photos) {
      collectedData.installationBoxImage = photos.installationBoxImage;
      collectedData.antennaImage = photos.antennaImage;
      collectedData.schemeImage = photos.schemeImage;
      collectedData.heatingCollectorImage = photos.heatingCollectorImage;
      collectedData.postInstallBoilerRoomImages =
        photos.postInstallBoilerRoomImages;
      collectedData.postInstallHeatingImages = photos.postInstallHeatingImages;
    }

    // Map Sensors Counter data
    const sensorsCounter = shareService.sensorsCounter;
    if (sensorsCounter) {
      collectedData.meterSensors = sensorsCounter.counterSensors;
    }

    // Map Sensors Other data
    const sensorsOther = shareService.sensorsOther;
    if (sensorsOther) {
      collectedData.otherSensors = sensorsOther.otherSensors;
      collectedData.pressureSensors = sensorsOther.pressureSensors;
      collectedData.pfcSensors = sensorsOther.pfcSensors;
    }

    // Map Sensors Outside Temperature data
    const sensorsOutsideTemp = shareService.sensorsOutsideTemp;
    if (sensorsOutsideTemp) {
      sensorsOutsideTemp.otmModules.forEach((otmModule) => {
        const index = collectedData.uvsModules.findIndex(
          (module) => module.modulId === otmModule.modulId,
        );
        if (index > -1) {
          collectedData.uvsModules[index] = otmModule;
        }
      });
    }

    // Map Sensors Pump data
    const sensorsPump = shareService.sensorsPump;
    if (sensorsPump) {
      collectedData.pumpSensors = sensorsPump.pumpSensors.map((sensor) => {
        return {
          sensorId: sensor.sensorId,
          name: sensor.name,
          minValue: sensor.minValue,
          sensorType: sensor.sensorType,
          value: sensor.value,
          image: sensor.image,
          pumpBrand: sensor.pumpBrand,
          pumpType: sensor.pumpType,
          pumpElectronicallyControlled: sensor.pumpElectronicallyControlled,
        } as PumpSensorModel;
      });
    }

    // Map Sensors Temperature data
    const sensorsTemperature = shareService.sensorsTemperature;
    if (sensorsTemperature) {
      sensorsTemperature.temperatureSensorsHelperList.forEach((sensor) => {
        collectedData.temperatureSensors.push({
          sensorId: sensor.sensorId,
          name: sensor.name,
          value: sensor.value,
          image: sensor.photo,
        });

        if (sensor.pairMode !== SensorModeType.SINGLE) {
          collectedData.temperatureSensors.push({
            sensorId: sensor.sensor2Id,
            name: sensor.name2,
            value: sensor.value2,
            image: sensor.photo2,
          });
        }

        if (sensor.pairMode === SensorModeType.TRIO) {
          collectedData.temperatureSensors.push({
            sensorId: sensor.sensor3Id,
            name: sensor.name3,
            value: sensor.value3,
            image: sensor.photo3,
          });
        }
      });
    }

    return collectedData;
  }
}

export class HeatingModel {
  heatingType: string;

  constructionYear: string;
  manufacturer: string;
  heatingTypeImage: string;
  heatingSystemImage: string;
  serialNumber?: string;
  heatingPower?: number;
}

export class HeatingCheckModel {
  averageRoomTemperature: number | null;
  outdoorTemperatureAvailable: boolean | null;
  summerWinterOperationAvailable: boolean | null;
  emergencyStopAvailable: boolean | null;
  insulatedPipes: boolean | null;
  insulatingCaps: boolean | null;
}

export class MaterialModel {
  materialType: 'COMMISSIONING' | 'INSTALLATION';
  nymCable: number;
  controlCable: number;
  shieldedCable: number;
  emptyConduit: number;
  junctionBoxes: number;
  countTemperatureSensors: number;
  countElectricityClamps: number;
  countMeters: number;
  countModules: number;
}
